/**
 * Pure evaluation layer for Bases: build rows from index entries, filter via
 * FilterGroup, multi-key sort, and value coercion/formatting helpers.
 * No vault/worker imports — unit-testable in node.
 */
import dayjs from 'dayjs';
import { dirname, titleFromPath, type VaultFileEntry } from '@rbv/shared';
import type { NoteIndexEntry } from '../worker/api.js';
import { TAGS_PROP, isGroup, type FilterGroup, type FilterRule, type SortKey } from './config.js';

export interface BaseRow {
  id: string;
  path: string;
  /** Flattened frontmatter props + file.* pseudo-props + tags. */
  props: Record<string, unknown>;
  tags: string[];
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' &&
  v !== null &&
  !Array.isArray(v) &&
  !(v instanceof Date) &&
  (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);

/** Flatten nested frontmatter objects into dot-joined keys ("a.b"). */
export function flattenProps(
  fm: Record<string, unknown> | null | undefined,
  out: Record<string, unknown> = {},
  prefix = '',
): Record<string, unknown> {
  if (!fm) return out;
  for (const [k, v] of Object.entries(fm)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (isPlainObject(v)) flattenProps(v, out, key);
    else out[key] = v;
  }
  return out;
}

export function buildRow(
  id: string,
  entry: Pick<VaultFileEntry, 'path' | 'ctime'>,
  note: Pick<NoteIndexEntry, 'frontmatter' | 'tags'> | null,
): BaseRow {
  const props = flattenProps(note?.frontmatter ?? null);
  const tags = note?.tags ?? [];
  // file.* pseudo-props are authoritative over same-named frontmatter keys.
  props['file.name'] = titleFromPath(entry.path);
  props['file.path'] = entry.path;
  props['file.folder'] = dirname(entry.path);
  props['file.ctime'] = entry.ctime;
  props[TAGS_PROP] = tags;
  return { id, path: entry.path, props, tags };
}

/** All frontmatter property keys seen across rows (excludes file.* / tags). */
export function discoverProps(rows: BaseRow[]): string[] {
  const keys = new Set<string>();
  for (const r of rows) {
    for (const k of Object.keys(r.props)) {
      if (k === TAGS_PROP || k.startsWith('file.')) continue;
      keys.add(k);
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

// ---------------------------------------------------------------- filtering

const DATEISH = /^\d{4}-\d{2}-\d{2}([T ].*)?$/;
const NUMERIC = /^-?\d+(\.\d+)?$/;

/** Coerce a value to a number for ordering: numbers as-is, ISO-ish date
 * strings and Dates to epoch ms, numeric strings to numbers; else null. */
export function comparableNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') {
    const s = v.trim();
    if (NUMERIC.test(s)) return Number(s);
    if (DATEISH.test(s)) {
      const d = dayjs(s);
      if (d.isValid()) return d.valueOf();
    }
  }
  return null;
}

const asText = (v: unknown): string => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map((x) => asText(x)).join(', ');
  return String(v);
};

const hasValue = (v: unknown): boolean => {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

const eqOne = (v: unknown, s: string): boolean => {
  if (typeof v === 'boolean') return String(v) === s.trim().toLowerCase();
  const nv = comparableNumber(v);
  const ns = comparableNumber(s);
  if (nv !== null && ns !== null) return nv === ns;
  return asText(v).trim().toLowerCase() === s.trim().toLowerCase();
};

const containsOne = (v: unknown, s: string): boolean =>
  asText(v).toLowerCase().includes(s.trim().toLowerCase());

const normTag = (s: string): string => s.trim().replace(/^#/, '').toLowerCase();

/** tag matches value exactly or as a path prefix ("a" matches "a/b"). */
const tagMatches = (tag: string, v: string): boolean => {
  const t = tag.toLowerCase();
  const n = normTag(v);
  return n !== '' && (t === n || t.startsWith(`${n}/`));
};

function orderCompare(v: unknown, ruleValue: string): number | null {
  if (!hasValue(v)) return null;
  const nv = comparableNumber(v);
  const ns = comparableNumber(ruleValue);
  if (nv !== null && ns !== null) return nv - ns;
  return asText(v).toLowerCase().localeCompare(ruleValue.trim().toLowerCase());
}

export function matchRule(rule: FilterRule, row: BaseRow): boolean {
  const v = row.props[rule.prop];
  const s = rule.value ?? '';

  if (rule.cmp === 'exists') return hasValue(v);
  if (rule.cmp === 'missing') return !hasValue(v);

  // tags: contains semantics over the tag list, including path prefixes.
  if (rule.prop === TAGS_PROP) {
    switch (rule.cmp) {
      case 'eq':
        return row.tags.some((t) => tagMatches(t, s));
      case 'ne':
        return !row.tags.some((t) => tagMatches(t, s));
      case 'contains':
        return row.tags.some((t) => tagMatches(t, s) || t.toLowerCase().includes(normTag(s)));
      default:
        return false;
    }
  }

  switch (rule.cmp) {
    case 'eq':
      return Array.isArray(v) ? v.some((x) => eqOne(x, s)) : eqOne(v, s);
    case 'ne':
      return !(Array.isArray(v) ? v.some((x) => eqOne(x, s)) : eqOne(v, s));
    case 'contains':
      return Array.isArray(v) ? v.some((x) => containsOne(x, s)) : containsOne(v, s);
    case 'gt': {
      const c = orderCompare(v, s);
      return c !== null && c > 0;
    }
    case 'gte': {
      const c = orderCompare(v, s);
      return c !== null && c >= 0;
    }
    case 'lt': {
      const c = orderCompare(v, s);
      return c !== null && c < 0;
    }
    case 'lte': {
      const c = orderCompare(v, s);
      return c !== null && c <= 0;
    }
    default:
      return false;
  }
}

export function matchGroup(g: FilterGroup, row: BaseRow): boolean {
  if (g.rules.length === 0) return true;
  const results = g.rules.map((r) => (isGroup(r) ? matchGroup(r, row) : matchRule(r, row)));
  return g.op === 'and' ? results.every(Boolean) : results.some(Boolean);
}

// ------------------------------------------------------------------ sorting

/** Typed pairwise compare; missing values sort last regardless of dir. */
function compareValues(a: unknown, b: unknown): number {
  const na = comparableNumber(a);
  const nb = comparableNumber(b);
  if (na !== null && nb !== null) return na - nb;
  return asText(a).localeCompare(asText(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows(rows: BaseRow[], sort: SortKey[]): BaseRow[] {
  if (sort.length === 0) return rows;
  const out = [...rows];
  out.sort((ra, rb) => {
    for (const key of sort) {
      const a = ra.props[key.prop];
      const b = rb.props[key.prop];
      const am = hasValue(a);
      const bm = hasValue(b);
      if (!am && !bm) continue;
      if (!am) return 1; // missing last, regardless of dir
      if (!bm) return -1;
      const c = compareValues(a, b);
      if (c !== 0) return key.dir === 'desc' ? -c : c;
    }
    return ra.path.localeCompare(rb.path);
  });
  return out;
}

// ----------------------------------------------------------- cell helpers

export interface WikilinkValue {
  target: string;
  display: string;
}

const WIKI_VALUE = /^\s*!?\[\[([^\][\n]+)\]\]\s*$/;

/** Best-effort: a cell value shaped like "[[Note]]" / "[[Note|Alias]]". */
export function parseWikilinkValue(v: unknown): WikilinkValue | null {
  if (typeof v !== 'string') return null;
  const m = WIKI_VALUE.exec(v);
  if (!m) return null;
  let inner = m[1];
  let display: string | undefined;
  const pipe = inner.indexOf('|');
  if (pipe !== -1) {
    display = inner.slice(pipe + 1).trim() || undefined;
    inner = inner.slice(0, pipe);
  }
  // strip #heading / #^block anchors for resolution
  const hash = inner.indexOf('#');
  const target = (hash === -1 ? inner : inner.slice(0, hash)).trim();
  if (!target) return null;
  return { target, display: display ?? inner.trim() };
}

export const isDateish = (v: unknown): v is string => typeof v === 'string' && DATEISH.test(v);

/** Human formatting for date-ish values (ISO strings, epoch ms for file.ctime). */
export function formatDate(v: string | number): string {
  const d = dayjs(v);
  if (!d.isValid()) return String(v);
  const hasTime = typeof v === 'number' || /[T ]\d/.test(String(v));
  return hasTime ? d.format('MMM D, YYYY HH:mm') : d.format('MMM D, YYYY');
}

/** Coerce an inline-edit input string to a typed frontmatter value. */
export function coerceInput(raw: string, prev: unknown): unknown {
  const s = raw.trim();
  if (typeof prev === 'boolean') {
    if (/^(true|yes|1)$/i.test(s)) return true;
    if (/^(false|no|0)$/i.test(s)) return false;
    return raw;
  }
  if (s !== '' && NUMERIC.test(s)) return Number(s);
  if (s === 'true') return true;
  if (s === 'false') return false;
  return raw;
}
